import { useContext, type KeyboardEvent } from "react";
import type {
    BoardCardShortcut,
    BoardColumnDef,
    BoardLaneDef,
} from "./Board.types";
import type { BoardCells } from "./boardCells";
import { getExpandedIds, type BoardCollapse } from "./boardCollapse";
import { locateBoardEntry } from "./boardMove";
import { getExpandedItemIds } from "./boardSelection";
import { isPlaceModifier } from "./boardSensors";
import { getCardKey } from "./useBoardRovingFocus";
import {
    BoardSelectionContext,
    type BoardSelection,
} from "./useBoardSelection";

export interface UseBoardCardKeysArgs<TItem> {
    cells: BoardCells<TItem>;
    columns: readonly BoardColumnDef[];
    lanes?: readonly BoardLaneDef[];
    collapse: BoardCollapse;
    isDragActive: boolean;
    cardShortcuts: readonly BoardCardShortcut[];
}

type BoardCardCommand =
    | { kind: "toggle" }
    | { kind: "selectAll" }
    | { kind: "shortcut"; shortcut: BoardCardShortcut };

const hasModifier = (event: KeyboardEvent) =>
    event.altKey || event.ctrlKey || event.metaKey;

const isKey = (event: KeyboardEvent, key: string) =>
    event.key.toLowerCase() === key.toLowerCase();

const getCommand = (
    event: KeyboardEvent,
    selection: BoardSelection | null,
    cardShortcuts: readonly BoardCardShortcut[],
): BoardCardCommand | null => {
    if (
        selection !== null &&
        isPlaceModifier(event) &&
        !event.altKey &&
        !event.shiftKey &&
        isKey(event, "a")
    ) {
        return { kind: "selectAll" };
    }
    if (hasModifier(event)) {
        return null;
    }
    if (selection !== null && isKey(event, "x")) {
        return { kind: "toggle" };
    }
    const shortcut = cardShortcuts.find(({ key }) => isKey(event, key));
    return shortcut === undefined ? null : { kind: "shortcut", shortcut };
};

const getTargetItemIds = (
    selection: BoardSelection | null,
    itemId: string,
): ReadonlySet<string> =>
    selection?.selectedItemIds.has(itemId)
        ? selection.selectedItemIds
        : new Set([itemId]);

/**
 * The board's keys on the focused card: X toggles it, ⌘A (Ctrl+A off Mac)
 * selects every card in expanded columns and lanes, and each of
 * `cardShortcuts` fires on its key. Only from a card's root, never from a
 * control or popup in it, and never during a drag. A key the board takes
 * stops there, held or not: app shortcuts (`useKeyboardCommand`) listen on
 * the document, and the focused card wins over them. Also hands roving
 * focus the selection's Shift+arrow handler.
 */
export const useBoardCardKeys = <TItem>({
    cells,
    columns,
    lanes,
    collapse,
    isDragActive,
    cardShortcuts,
}: UseBoardCardKeysArgs<TItem>) => {
    const selection = useContext(BoardSelectionContext);
    const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
        const { target } = event;
        if (
            isDragActive ||
            event.defaultPrevented ||
            !(target instanceof HTMLElement)
        ) {
            return;
        }
        const placementKey = getCardKey(target);
        const location =
            placementKey === null
                ? null
                : locateBoardEntry(cells, placementKey);
        const command =
            location === null
                ? null
                : getCommand(event, selection, cardShortcuts);
        if (placementKey === null || location === null || command === null) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        if (event.repeat) {
            return;
        }
        if (command.kind === "toggle") {
            selection?.toggleCard(placementKey);
        } else if (command.kind === "selectAll") {
            selection?.selectItems(
                getExpandedItemIds(
                    cells,
                    getExpandedIds({ columns, lanes, collapse }),
                ),
            );
        } else {
            const { itemId } = location.entry;
            command.shortcut.onTrigger({
                itemIds: getTargetItemIds(selection, itemId),
                itemId,
                card: target,
                board: event.currentTarget,
            });
        }
    };
    return { onKeyDown, onExtend: selection?.extendTo };
};
